import { Navigate, useLocation } from "react-router-dom";
import { useWorkspaces } from "../identity";
import type { Workspace } from "../identity";
import { BootScreen } from "../components/layout/DashboardBoot";
import { AppLoadingScreen } from "../components/layout/AppLoadingScreen";
import { readStoredWorkspaceId } from "./storage";

/** Last-used workspace if still accessible, else the first shared one (a
 *  personal workspace is the fallback, not the default landing). */
export function pickDefaultWorkspace(
  workspaces: Workspace[],
): Workspace | undefined {
  const stored = readStoredWorkspaceId();
  return (
    workspaces.find((w) => w.id === stored) ??
    workspaces.find((w) => w.type !== "personal") ??
    workspaces[0]
  );
}

/* Resolves a workspace-less URL to the default workspace. Mounted at "/"
 * (suffix none) and at the legacy pre-workspace routes ("/agents/x" →
 * /w/<id>/agents/x, search preserved) so old links keep working.
 *
 * Also the single owner of the invitation hand-off: every gated entry funnels
 * through here (the app root), so an invite deeplink — /?invitation_id=…,
 * whether the viewer just logged in or was already authenticated — is forwarded
 * to the workspace-less /invite/:id accept page before any workspace is
 * resolved (a brand-new invitee may not have one yet). */
export function WorkspaceRedirect({ legacy = false }: { legacy?: boolean }) {
  const { data: workspaces, error, initialLoading } = useWorkspaces();
  const location = useLocation();

  const invitationId = new URLSearchParams(location.search).get(
    "invitation_id",
  );
  if (invitationId) {
    return (
      <Navigate to={`/invite/${encodeURIComponent(invitationId)}`} replace />
    );
  }

  if (error) {
    return (
      <BootScreen>
        <p className="text-body-sm text-error-fg">
          Couldn’t load your workspaces. Check your connection and reload.
        </p>
      </BootScreen>
    );
  }

  if (initialLoading || !workspaces) {
    return <AppLoadingScreen />;
  }

  const target = pickDefaultWorkspace(workspaces);
  if (!target) {
    // The identity service normally auto-provisions a personal workspace, so
    // this is a degenerate state — but it must not white-screen.
    return (
      <BootScreen>
        <div className="flex max-w-sm flex-col items-center gap-2 text-center">
          <p className="text-body font-medium text-fg-1">No workspaces yet</p>
          <p className="text-body-sm text-fg-2">
            Your account has no workspaces. Ask your organization admin for
            access, then reload.
          </p>
        </div>
      </BootScreen>
    );
  }

  const suffix = legacy ? `${location.pathname}${location.search}` : "";
  return (
    <Navigate to={`/w/${encodeURIComponent(target.id)}${suffix}`} replace />
  );
}
